import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Loader2, UserPlus, Check, ImageOff } from 'lucide-react';
import { getAgentBio, type AgentBio, type PublicAgent } from '../../services/agentService';
import { getAgentGallery } from '../../services/galleryService';
import { TYPE } from '../../constants/typeScale';
import { useAgentImage } from '../../hooks/useAgentImage';

/**
 * CASTING CAROUSEL — audition ONE agent before hiring them onto a production.
 *
 * The face rail answers "who is available"; it cannot answer "is this the right actor for my series". A creator
 * casting a lead for 60 episodes needs to SEE them properly — several images, at size — and read who they are.
 * So this is a large 9:16 portrait, their other frames, the bio beside it, and one deliberate Hire action.
 *
 * It owns exactly one axis: the PHOTOS of the agent it was handed. Walking to a DIFFERENT candidate belongs to
 * whoever owns the candidate list (`AgentHiringModal` + its bench) — the two used to be identical chevron pairs
 * in one dialog, which is precisely why neither read as anything (owner 2026-08-23). So the photo control now
 * lives ON the picture, as one pill carrying a photo count: it can only be read as belonging to the image.
 *
 * Presentational-ish: fetches the profile it is showing, hands the hire decision back via callback.
 */
const CastingCarousel: React.FC<{
  agent: PublicAgent;
  /** Already holding the seat — the action becomes a state, not a button. */
  alreadyCast?: boolean;
  hiring?: boolean;
  /** The hire verb ("Cast", "Hire") — every seat is a hiring process, but the word differs. */
  verb?: string;
  /** Label when the seat is already theirs ("In your cast", "Your writer"). */
  hiredLabel?: string;
  /** What this candidate brings to the seat being filled ("Kinetic short-form · Fast, punchy cutting") — the
   *  craft line a creator actually decides on, which a public bio does not carry. */
  note?: string;
  onHire: (agent: PublicAgent) => void;
}> = ({ agent, alreadyCast, hiring, verb = 'Cast', hiredLabel = 'In your cast', note, onHire }) => {
  const [bio, setBio] = useState<AgentBio | null>(null);
  const [shots, setShots] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  // Reset to the first image whenever the agent changes, so a browse never opens mid-gallery.
  useEffect(() => { setIdx(0); }, [agent.id]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([getAgentBio(agent.id), getAgentGallery(agent.id)])
      .then(([b, g]) => {
        if (!alive) return;
        setBio(b.status === 'fulfilled' ? b.value : null);
        const posts = g.status === 'fulfilled' ? (g.value?.posts || []) : [];
        // Only shots we can actually show — a locked or image-less post is not an audition photo.
        setShots(posts.map((p) => p.image_path).filter((p): p is string => !!p));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [agent.id]);

  // The avatar leads (it is the vetted, face-forward shot the render locks to), then the gallery.
  const frames = useMemo(
    () => [agent.avatar_image, ...shots].filter((s): s is string => !!s),
    [agent.avatar_image, shots],
  );
  const current = frames[Math.min(idx, Math.max(frames.length - 1, 0))] || null;
  const imageSrc = useAgentImage(current);

  const step = useCallback((by: number) => {
    setIdx((i) => (frames.length ? (i + by + frames.length) % frames.length : 0));
  }, [frames.length]);

  const photoBtn = `flex h-11 w-11 items-center justify-center rounded-full text-white/90 transition-colors
    hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white`;

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800">
      <div className="flex flex-col gap-4 p-4 sm:flex-row">
        {/*
          THE FRAME — SQUARE, AND NO BIGGER THAN THE PICTURE IT HOLDS (owner 2026-09-26: *"maybe make the
          frame for enlarge photo smaller? and square?"*).

          It was 9:16 at 220 wide, "because that is what we shoot". True of the finished film, wrong for this
          dialog: the picture it actually shows is the agent's AVATAR, which exists only at 256x256 — every
          set on disk and the one staging serves. A square source filling a 9:16 box gets scaled up about 1.5x
          and loses nearly half its width to the crop, which is exactly the blur that was reported, and it is
          the same in production. No CSS adds detail back, so the honest fix is to stop asking the frame for
          pixels that were never generated.

          Capped at 256 so the avatar renders at or under its native size — sharp by construction rather than
          by luck, and it stays sharp if the avatars are ever regenerated larger. A gallery shot (natively
          9:16, 2304 wide) is centre-cropped into the square and still downscales, so both sources are clean;
          the photo pill pages between them.
        */}
        <div className="relative mx-auto aspect-square w-full max-w-[256px] shrink-0 overflow-hidden rounded-xl bg-secondary-100 dark:bg-secondary-800">
          {imageSrc ? (
            <img src={imageSrc} alt={`${agent.name}, frame ${idx + 1} of ${frames.length}`}
                 className="h-full w-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-secondary-400">
              {loading
                ? <Loader2 className="h-5 w-5 animate-spin" />
                : <><ImageOff className="h-5 w-5" /><span className={TYPE.meta}>No photos yet</span></>}
            </div>
          )}

          {/* THE PHOTO AXIS — one pill, on the picture, carrying the photo count so it cannot be mistaken for
              navigation of the dialog. Only shown when there is more than one frame to see. */}
          {frames.length > 1 && (
            <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/70 to-transparent pb-1.5 pt-6">
              <div className="flex items-center gap-0.5 rounded-full bg-black/55 px-0.5 backdrop-blur-sm">
                <button type="button" onClick={() => step(-1)} className={photoBtn}
                  aria-label={`Show previous photo of ${agent.name}`} title="Previous photo">
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                </button>
                <span className={`${TYPE.micro} flex items-center gap-1 font-medium text-white/90`}>
                  <ImageIcon className="h-3 w-3" aria-hidden />{idx + 1}/{frames.length}
                </span>
                <button type="button" onClick={() => step(1)} className={photoBtn}
                  aria-label={`Show next photo of ${agent.name}`} title="Next photo">
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* WHO THEY ARE — the reason to pick this actor over the next one. */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h3 className={`${TYPE.stageTitle} truncate text-secondary-900 dark:text-white`}>{agent.name}</h3>
            <span className={`${TYPE.control} truncate text-secondary-400`}>{agent.persona}</span>
          </div>

          {/* What they bring to THIS seat — the craft the catalog knows about, above the general bio. */}
          {note && <p className={`${TYPE.control} mt-1 font-medium text-secondary-700 dark:text-secondary-200`}>{note}</p>}

          {loading && !bio ? (
            <p className={`${TYPE.control} mt-2 text-secondary-400`}>Reading their profile…</p>
          ) : (
            <div className="mt-2 space-y-1.5">
              {(bio?.description || agent.description) && (
                <p className={`${TYPE.control} leading-relaxed text-secondary-600 dark:text-secondary-300`}>{bio?.description || agent.description}</p>
              )}
              {(bio?.personality || agent.personality) && (
                <p className={`${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
                  <span className="font-medium text-secondary-700 dark:text-secondary-200">Personality · </span>
                  {bio?.personality || agent.personality}
                </p>
              )}
              {bio?.appearance && (
                <p className={`${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
                  <span className="font-medium text-secondary-700 dark:text-secondary-200">Look · </span>{bio.appearance}
                </p>
              )}
              {bio?.hobby && (
                <p className={`${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
                  <span className="font-medium text-secondary-700 dark:text-secondary-200">Off set · </span>{bio.hobby}
                </p>
              )}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            {alreadyCast ? (
              <span className={`${TYPE.control} inline-flex items-center gap-1.5 rounded-lg bg-secondary-100 px-3 py-1.5 font-medium text-secondary-500 dark:bg-secondary-800 dark:text-secondary-400`}>
                <Check className="h-3.5 w-3.5" aria-hidden /> {hiredLabel}
              </span>
            ) : (
              <button type="button" onClick={() => onHire(agent)} disabled={hiring}
                className={`${TYPE.control} inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 px-3 py-1.5 font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-secondary-900`}>
                {hiring ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <UserPlus className="h-3.5 w-3.5" aria-hidden />}
                {hiring ? 'One moment…' : `${verb} ${agent.name}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CastingCarousel;
